import { LoadingOutlined } from '@ant-design/icons';
import { useResizeObserver } from '@aibi-ui/core';
import { debounce } from 'lodash';
import moment from 'moment';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  VariableSizeList as List,
  ListOnItemsRenderedProps,
} from 'react-window';
import ListHeader from './header/ListHeader';

interface ListItemProps<T> {
  index: number;
  style: React.CSSProperties;
  data: {
    items: {
      title: string;
      list: T[];
    }[];
    setSize: (index: number, size: number) => void;
    renderItem: ({
      data,
      showDelete,
    }: {
      data: T;
      showDelete: boolean;
    }) => React.ReactNode;
    showDelete: boolean;
  };
}

const ListItem = <T,>({ index, style, data }: ListItemProps<T>) => {
  const { items, setSize, renderItem, showDelete } = data;
  const item = items[index];
  const ref = useRef<HTMLDivElement>(null);
  const onResize = debounce((height: number) => {
    setSize(index, height);
  }, 16);
  useResizeObserver(ref, ({ height }) => onResize(height));
  style = { ...style, width: 'calc(100% - 32px)', left: '16px' };
  if (!item) {
    return (
      <div ref={ref} style={style} className="flex items-center justify-center">
        <LoadingOutlined spin />
      </div>
    );
  }
  return (
    <div style={style}>
      <div ref={ref} className="flex flex-col gap-[12px]">
        <span className="text-14px text-[#999] px-[6px] pt-[15px] sticky top-0 bg-[#f3f4f6] z-[999]">
          {item.title}
        </span>
        {item.list.map(conversation => {
          return renderItem({ data: conversation, showDelete });
        })}
      </div>
    </div>
  );
};

export interface ListContainerProps<T, KeyType extends keyof T> {
  conversationList: T[];
  finished: boolean;
  loadMore: () => Promise<void> | undefined;
  empty?: React.ReactNode;
  onBatchDelete: (ids: T[KeyType][]) => void;
  onSearch: (value: string) => void;
  renderItem: (props: { data: T; showDelete: boolean }) => React.ReactNode;
  rowKey: KeyType;
  loading: boolean;
  checkedList: T[KeyType][];
  setCheckedList: Dispatch<SetStateAction<T[KeyType][]>>;
}

const ListContainer = <T extends { time: string }, KeyType extends keyof T>({
  conversationList,
  finished,
  loadMore,
  empty,
  onBatchDelete,
  onSearch,
  renderItem,
  rowKey,
  loading,
  checkedList,
  setCheckedList,
}: ListContainerProps<T, KeyType>) => {
  const [showDelete, setShowDelete] = useState(false);
  useEffect(() => {
    setCheckedList([]);
  }, [showDelete]);
  const allChecked = useMemo(() => {
    return (
      checkedList.length > 0 && checkedList.length === conversationList.length
    );
  }, [checkedList, conversationList]);
  const setAllChecked = (checked: boolean) => {
    if (checked) {
      setCheckedList(conversationList.map(item => item[rowKey]));
    } else {
      setCheckedList([]);
    }
  };

  const categoryList = useMemo(() => {
    const now = moment();
    const categoryMap = conversationList.reduce(
      (result, conversation) => {
        const { time } = conversation;
        const dateMoment = moment(time);
        if (dateMoment.isValid()) {
          let key = '';
          if (dateMoment.isAfter(now.clone().subtract(7, 'days'))) {
            key = '7天内';
          } else if (dateMoment.isAfter(now.clone().subtract(30, 'days'))) {
            key = '30天内';
          } else {
            key = '30天之前';
          }
          const list = result[key] || [];
          list.push(conversation);
          result[key] = list;
        }
        return result;
      },
      {} as Record<string, T[]>,
    );
    return Object.keys(categoryMap).map(k => {
      return {
        title: k,
        list: categoryMap[k],
      };
    });
  }, [conversationList]);
  const listRef = useRef<List>(null);
  const sizeMap = useRef<Record<number, number>>({});
  const getItemSize = (index: number) => {
    return sizeMap.current[index] || 56;
  };
  const setSize = (index: number, size: number) => {
    if (sizeMap.current[index] !== size) {
      sizeMap.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    }
  };
  const itemCount = useMemo(() => {
    return categoryList.length + (finished ? 0 : 1);
  }, [categoryList, finished]);

  // 触底加载逻辑
  const loadingRef = useRef(false);
  const handleItemsRendered = useCallback(
    ({ overscanStopIndex }: ListOnItemsRenderedProps) => {
      if (finished || loading) return;
      if (loadingRef.current) return;
      if (overscanStopIndex >= itemCount - 1 && !finished) {
        loadingRef.current = true;
        Promise.resolve(loadMore()).finally(() => {
          loadingRef.current = false;
        });
      }
    },
    [finished, itemCount, loadMore, loading],
  );
  return (
    <div className="flex-1 flex flex-col overflow-auto">
      <div className="px-[16px]">
        <ListHeader
          showDelete={showDelete}
          setShowDelete={setShowDelete}
          setAllChecked={setAllChecked}
          allChecked={allChecked}
          onBatchDelete={() => onBatchDelete(checkedList)}
          onSearch={onSearch}
          canDelete={checkedList.length > 0}
        />
      </div>
      <div className="flex-1 overflow-auto">
        <div className={['h-full overflow-auto'].join(' ')}>
          {!loadingRef.current &&
            conversationList.length === 0 &&
            (empty || (
              <div className="flex justify-center my-[12px] text-[#888]">暂无数据</div>
            ))}
          {conversationList.length > 0 && (
            <AutoSizer>
              {({ height, width }) => (
                <List<ListItemProps<T>['data']>
                  ref={listRef}
                  height={height}
                  width={width}
                  itemCount={itemCount}
                  itemSize={getItemSize}
                  itemData={{
                    items: categoryList,
                    setSize,
                    renderItem,
                    showDelete,
                  }}
                  onItemsRendered={handleItemsRendered}
                >
                  {ListItem}
                </List>
              )}
            </AutoSizer>
          )}
        </div>
      </div>
    </div>
  );
};
export default ListContainer;
